import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  User,
  Shield,
  Zap,
  Download,
  Key,
  Check,
  ChevronRight,
  ChevronLeft,
  Plus,
  Trash2,
  Globe,
  Mail,
  FileWarning,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import MSPLayout from '../components/MSPLayout';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const steps = [
  { id: 'company_info', title: 'Client Info', icon: Building2 },
  { id: 'plan_selection', title: 'Select Plan', icon: Shield },
  { id: 'admin_user', title: 'Admin User', icon: User },
  { id: 'security_policies', title: 'Security', icon: Shield },
  { id: 'automation_rules', title: 'Automation', icon: Zap },
  { id: 'agent_deployment', title: 'Deployment', icon: Download },
  { id: 'portal_access', title: 'Complete', icon: Key },
];

const industries = [
  'Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Retail', 'Education', 'Legal', 'Other'
];

const companySizes = ['1-10', '11-50', '51-200', '201-500', '500+'];

const defaultRules = [
  { name: 'Block malicious files', trigger_type: 'file_malicious', condition: 'risk_score >= 80', action: 'quarantine', enabled: true },
  { name: 'Alert on DNS threats', trigger_type: 'dns_block', condition: 'classification == malicious', action: 'notify', enabled: true },
  { name: 'Quarantine phishing emails', trigger_type: 'email_phishing', condition: 'risk_score >= 70', action: 'quarantine', enabled: true },
];

export default function MSPClientOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clientId, setClientId] = useState(null);
  
  // Form data
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    industry: 'Technology',
    company_size: '1-10',
    contact_email: '',
    contact_phone: '',
  });
  
  const [selectedPlan, setSelectedPlan] = useState('professional');
  
  const [adminUser, setAdminUser] = useState({
    admin_name: '',
    admin_email: '',
    admin_password: '',
    send_welcome_email: true,
  });
  
  const [securityPolicies, setSecurityPolicies] = useState({
    dns_policies: [],
    email_settings: {
      auto_quarantine_phishing: true,
      scan_attachments: true,
      block_suspicious_links: true,
    },
    file_settings: {
      auto_quarantine_malware: true,
      block_executables: false,
      scan_archives: true,
    },
  });
  
  const [dnsPolicy, setDnsPolicy] = useState({ name: '', domain_pattern: '', action: 'block' });
  
  const [automationRules, setAutomationRules] = useState(defaultRules);
  
  const [deployment, setDeployment] = useState({
    platforms: ['windows', 'macos'],
    generate_installer: true,
  });
  
  const [installerToken, setInstallerToken] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('msp_token');
    return { Authorization: `Bearer ${token}` };
  };

  const handleStartOnboarding = async () => {
    if (!companyInfo.company_name || !companyInfo.contact_email) {
      toast.error('Please fill in company name and contact email');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/msp/onboarding/start`,
        companyInfo,
        { headers: getAuthHeaders() }
      );
      setClientId(response.data.client_id);
      setCurrentStep(1);
      toast.success('Client created! Continue with setup.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/msp/onboarding/${clientId}/plan`,
        { plan: selectedPlan },
        { headers: getAuthHeaders() }
      );
      setCurrentStep(2);
    } catch (error) {
      toast.error('Failed to select plan');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!adminUser.admin_name || !adminUser.admin_email || !adminUser.admin_password) {
      toast.error('Please fill in all admin user fields');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/msp/onboarding/${clientId}/admin`,
        adminUser,
        { headers: getAuthHeaders() }
      );
      setCurrentStep(3);
      toast.success('Admin user created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePolicies = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/msp/onboarding/${clientId}/policies`,
        securityPolicies,
        { headers: getAuthHeaders() }
      );
      setCurrentStep(4);
    } catch (error) {
      toast.error('Failed to save policies');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRules = async () => {
    setLoading(true);
    try {
      const enabledRules = automationRules.filter(r => r.enabled);
      await axios.post(
        `${API}/api/msp/onboarding/${clientId}/rules`,
        { automation_rules: enabledRules },
        { headers: getAuthHeaders() }
      );
      setCurrentStep(5);
      toast.success(`${enabledRules.length} automation rules created!`);
    } catch (error) {
      toast.error('Failed to save rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupDeployment = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/msp/onboarding/${clientId}/deploy`,
        deployment,
        { headers: getAuthHeaders() }
      );
      setInstallerToken(response.data.installer_token);
      setCurrentStep(6);
    } catch (error) {
      toast.error('Failed to setup deployment');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/msp/onboarding/${clientId}/complete`,
        { send_credentials: true },
        { headers: getAuthHeaders() }
      );
      toast.success('Client onboarding completed!');
      navigate(`/msp/clients/${clientId}`);
    } catch (error) {
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const addDnsPolicy = () => {
    if (!dnsPolicy.name || !dnsPolicy.domain_pattern) {
      toast.error('Please fill in policy name and domain pattern');
      return;
    }
    setSecurityPolicies({
      ...securityPolicies,
      dns_policies: [...securityPolicies.dns_policies, dnsPolicy]
    });
    setDnsPolicy({ name: '', domain_pattern: '', action: 'block' });
  };

  const removeDnsPolicy = (index) => {
    setSecurityPolicies({
      ...securityPolicies,
      dns_policies: securityPolicies.dns_policies.filter((_, i) => i !== index)
    });
  };

  const toggleRule = (index) => {
    const updated = [...automationRules];
    updated[index].enabled = !updated[index].enabled;
    setAutomationRules(updated);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white">New Client Setup</h2>
              <p className="text-slate-400 mt-1">Enter the client&apos;s company information</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Company Name *</Label>
                <Input
                  value={companyInfo.company_name}
                  onChange={(e) => setCompanyInfo({...companyInfo, company_name: e.target.value})}
                  placeholder="Client Company Inc"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Industry</Label>
                <Select value={companyInfo.industry} onValueChange={(v) => setCompanyInfo({...companyInfo, industry: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {industries.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Company Size</Label>
                <Select value={companyInfo.company_size} onValueChange={(v) => setCompanyInfo({...companyInfo, company_size: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {companySizes.map(s => <SelectItem key={s} value={s}>{s} employees</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Contact Email *</Label>
                <Input
                  type="email"
                  value={companyInfo.contact_email}
                  onChange={(e) => setCompanyInfo({...companyInfo, contact_email: e.target.value})}
                  placeholder="contact@client.com"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-slate-300">Contact Phone</Label>
                <Input
                  value={companyInfo.contact_phone}
                  onChange={(e) => setCompanyInfo({...companyInfo, contact_phone: e.target.value})}
                  placeholder="+1 (555) 123-4567"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button onClick={handleStartOnboarding} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? 'Creating...' : 'Continue'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white">Select Plan</h2>
              <p className="text-slate-400 mt-1">Choose a subscription plan for this client</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { id: 'professional', name: 'Professional', price: 299, seats: 10, features: ['10 users', '50 devices', 'Standard support'] },
                { id: 'enterprise', name: 'Enterprise', price: 599, seats: 15, features: ['15 users', 'Unlimited devices', 'Priority support'] },
              ].map((plan) => (
                <Card
                  key={plan.id}
                  className={`cursor-pointer transition-all ${
                    selectedPlan === plan.id
                      ? 'bg-blue-500/10 border-blue-500'
                      : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                  }`}
                  onClick={() => setSelectedPlan(plan.id)}
                >
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                    <div className="mt-2">
                      <span className="text-2xl font-bold text-white">${plan.price}</span>
                      <span className="text-slate-400">/month</span>
                    </div>
                    <p className="text-emerald-400 text-sm mt-1">Your commission: ${(plan.price * 0.3).toFixed(0)}/mo</p>
                    <ul className="mt-4 space-y-2">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                          <Check className="w-4 h-4 text-emerald-500" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => navigate('/msp/clients')} className="text-slate-400">
                Cancel
              </Button>
              <Button onClick={handleSelectPlan} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? 'Saving...' : 'Continue'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white">Create Admin User</h2>
              <p className="text-slate-400 mt-1">Set up the client&apos;s admin account</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Admin Name *</Label>
                <Input
                  value={adminUser.admin_name}
                  onChange={(e) => setAdminUser({...adminUser, admin_name: e.target.value})}
                  placeholder="John Smith"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Admin Email *</Label>
                <Input
                  type="email"
                  value={adminUser.admin_email}
                  onChange={(e) => setAdminUser({...adminUser, admin_email: e.target.value})}
                  placeholder="admin@client.com"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-slate-300">Temporary Password *</Label>
                <Input
                  type="password"
                  value={adminUser.admin_password}
                  onChange={(e) => setAdminUser({...adminUser, admin_password: e.target.value})}
                  placeholder="Min. 8 characters"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2 p-4 rounded-lg bg-slate-800">
              <Checkbox
                checked={adminUser.send_welcome_email}
                onCheckedChange={(v) => setAdminUser({...adminUser, send_welcome_email: v})}
              />
              <Label className="text-slate-300">Send welcome email with login credentials</Label>
            </div>
            
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setCurrentStep(1)} className="text-slate-400">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button onClick={handleCreateAdmin} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? 'Creating...' : 'Continue'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white">Security Policies</h2>
              <p className="text-slate-400 mt-1">Configure security settings for this client</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* DNS Policies */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    DNS Policies
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      value={dnsPolicy.name}
                      onChange={(e) => setDnsPolicy({...dnsPolicy, name: e.target.value})}
                      placeholder="Policy name"
                      className="bg-slate-900 border-slate-600 text-white text-sm"
                    />
                    <Input
                      value={dnsPolicy.domain_pattern}
                      onChange={(e) => setDnsPolicy({...dnsPolicy, domain_pattern: e.target.value})}
                      placeholder="*.domain.com"
                      className="bg-slate-900 border-slate-600 text-white text-sm"
                    />
                    <Button size="sm" onClick={addDnsPolicy} className="bg-blue-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {securityPolicies.dns_policies.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-slate-900 rounded">
                      <span className="text-sm text-slate-300">{p.name}: {p.domain_pattern}</span>
                      <Button size="sm" variant="ghost" onClick={() => removeDnsPolicy(i)} className="text-red-500 h-6">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Email Settings */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Protection
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(securityPolicies.email_settings).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <Label className="text-slate-300 text-sm">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Label>
                      <Switch
                        checked={value}
                        onCheckedChange={(v) => setSecurityPolicies({
                          ...securityPolicies,
                          email_settings: {...securityPolicies.email_settings, [key]: v}
                        })}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* File Settings */}
              <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <FileWarning className="w-4 h-4" />
                    File Protection
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(securityPolicies.file_settings).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between p-3 bg-slate-900 rounded">
                        <Label className="text-slate-300 text-sm">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Label>
                        <Switch
                          checked={value}
                          onCheckedChange={(v) => setSecurityPolicies({
                            ...securityPolicies,
                            file_settings: {...securityPolicies.file_settings, [key]: v}
                          })}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setCurrentStep(2)} className="text-slate-400">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button onClick={handleSavePolicies} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? 'Saving...' : 'Continue'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white">Automation Rules</h2>
              <p className="text-slate-400 mt-1">Enable automated threat response rules</p>
            </div>
            
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 space-y-3">
                {automationRules.map((rule, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-900 rounded">
                    <div className="flex items-center gap-3">
                      <Switch checked={rule.enabled} onCheckedChange={() => toggleRule(index)} />
                      <div>
                        <p className="text-white text-sm font-medium">{rule.name}</p>
                        <p className="text-slate-500 text-xs">{rule.trigger_type} → {rule.action}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="border-slate-600 text-xs">
                      {rule.condition}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
            
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setCurrentStep(3)} className="text-slate-400">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button onClick={handleSaveRules} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? 'Saving...' : 'Continue'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white">Agent Deployment</h2>
              <p className="text-slate-400 mt-1">Configure agent installers for client devices</p>
            </div>
            
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <Label className="text-slate-300 mb-3 block">Select Platforms</Label>
                <div className="flex flex-wrap gap-3">
                  {['windows', 'macos', 'linux'].map((platform) => (
                    <div
                      key={platform}
                      className={`px-4 py-3 rounded-lg cursor-pointer transition-all ${
                        deployment.platforms.includes(platform)
                          ? 'bg-blue-500/20 border border-blue-500'
                          : 'bg-slate-900 border border-slate-700'
                      }`}
                      onClick={() => {
                        const platforms = deployment.platforms.includes(platform)
                          ? deployment.platforms.filter(p => p !== platform)
                          : [...deployment.platforms, platform];
                        setDeployment({...deployment, platforms});
                      }}
                    >
                      <span className="text-white capitalize">{platform}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 flex items-center gap-2">
                  <Checkbox
                    checked={deployment.generate_installer}
                    onCheckedChange={(v) => setDeployment({...deployment, generate_installer: v})}
                  />
                  <Label className="text-slate-300">Generate custom installer with client configuration</Label>
                </div>
              </CardContent>
            </Card>
            
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setCurrentStep(4)} className="text-slate-400">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button onClick={handleSetupDeployment} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? 'Generating...' : 'Continue'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className="text-xl font-bold text-white">Client Setup Complete!</h2>
              <p className="text-slate-400 mt-1">The client is ready to use Clear Defense AI</p>
            </div>
            
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-slate-400">Company</span>
                  <span className="text-white font-medium">{companyInfo.company_name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-slate-400">Plan</span>
                  <Badge className="bg-blue-500/20 text-blue-400 capitalize">{selectedPlan}</Badge>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-slate-400">Admin Email</span>
                  <span className="text-white">{adminUser.admin_email}</span>
                </div>
                {installerToken && (
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <span className="text-slate-400">Installer Token</span>
                    <code className="text-emerald-400 font-mono">{installerToken}</code>
                  </div>
                )}
                <div className="flex justify-between py-2">
                  <span className="text-slate-400">Monthly Commission</span>
                  <span className="text-emerald-400 font-bold">
                    +${selectedPlan === 'enterprise' ? '179.70' : '89.70'}/mo
                  </span>
                </div>
              </CardContent>
            </Card>
            
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-blue-400 text-sm">
                A welcome email with login credentials has been sent to the client admin.
              </p>
            </div>
            
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => navigate('/msp/clients')} className="text-slate-400">
                Back to Clients
              </Button>
              <Button onClick={handleComplete} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
                {loading ? 'Completing...' : 'Complete Setup'}
                <Check className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <MSPLayout>
      <div className="max-w-4xl mx-auto">
        {/* Progress Steps */}
        <div className="mb-8 overflow-x-auto pb-2">
          <div className="flex justify-between items-center min-w-[600px]">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isCompleted = index < currentStep;
              const isCurrent = index === currentStep;
              
              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      isCompleted ? 'bg-emerald-500' :
                      isCurrent ? 'bg-blue-500' :
                      'bg-slate-800'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-4 h-4 text-white" />
                      ) : (
                        <Icon className={`w-4 h-4 ${isCurrent ? 'text-white' : 'text-slate-500'}`} />
                      )}
                    </div>
                    <span className={`mt-1 text-xs ${isCurrent ? 'text-white' : 'text-slate-500'}`}>
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 ${
                      index < currentStep ? 'bg-emerald-500' : 'bg-slate-800'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </CardContent>
        </Card>
      </div>
    </MSPLayout>
  );
}
